import React, { useState, useMemo } from 'react';
import { Staff } from '../types';
import { StaffSummary } from '../services/summary.service';
import Card from './ui/Card';
import Button from './ui/Button';
import Input from './ui/Input';
import Modal from './ui/Modal';
import { useAuth } from '../contexts/AuthContext';
import {
    Users,
    Search,
    Edit,
    Eye,
    Shield,
    ChevronLeft,
    ChevronRight,
    User,
    Mail,
    Phone,
    Calendar,
    DollarSign,
    Plus,
    ToggleLeft,
    ToggleRight,
    UserCheck,
    UserX,
    RefreshCw
} from 'lucide-react';

interface StaffViewProps {
    staff: Staff[];
    onEdit: (staff: Staff) => void;
    onToggleStatus: (staffId: string, newStatus: 'active' | 'inactive') => void;
    onAddStaff?: () => void;
    loading?: boolean;
    summary?: StaffSummary;
}

const StaffView: React.FC<StaffViewProps> = ({ staff, onEdit, onToggleStatus, onAddStaff, loading = false, summary }) => {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'super_admin';
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState<string>('all');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [viewingStaff, setViewingStaff] = useState<Staff | null>(null);

    // Filter and search staff
    const filteredStaff = useMemo(() => {
        return staff.filter(member => {
            const matchesSearch =
                member.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.staffId.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesRole = filterRole === 'all' || member.role === filterRole;
            const matchesStatus = filterStatus === 'all' || member.currentPosition.status === filterStatus;

            return matchesSearch && matchesRole && matchesStatus;
        });
    }, [staff, searchTerm, filterRole, filterStatus]);

    // Pagination
    const totalPages = Math.ceil(filteredStaff.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedStaff = filteredStaff.slice(startIndex, startIndex + itemsPerPage);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (value: string) => {
        setItemsPerPage(parseInt(value));
        setCurrentPage(1);
    };

    const getRoleBadgeColor = (role: string) => {
        switch (role) {
            case 'admin': return 'bg-red-100 text-red-800';
            case 'hr': return 'bg-blue-100 text-blue-800';
            case 'teacher': return 'bg-green-100 text-green-800';
            case 'accountant': return 'bg-purple-100 text-purple-800';
            case 'principal': return 'bg-yellow-100 text-yellow-800';
            case 'clerk': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'inactive': return 'bg-yellow-100 text-yellow-800';
            case 'resigned': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getAuthorityCount = (authorities: { [key: string]: boolean }) => {
        return Object.values(authorities).filter(Boolean).length;
    };

    const handleStatusToggle = (staffId: string, currentStatus: string) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        onToggleStatus(staffId, newStatus as 'active' | 'inactive');
    };

    const StaffCard: React.FC<{ member: Staff }> = ({ member }) => (
        <Card glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
            <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                            <User className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {member.firstName} {member.lastName}
                            </h3>
                            <p className="text-sm text-gray-600">{member.staffId}</p>
                        </div>
                    </div>
                    <div className="flex space-x-1">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setViewingStaff(member)}
                        >
                            <Eye className="h-4 w-4" />
                        </Button>
                        {isSuperAdmin ? (
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => onEdit(member)}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                        ) : (
                            <span title="Contact Dynamix Zone for access this" className="inline-block">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    className="opacity-40 cursor-not-allowed"
                                    disabled
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>
                            </span>
                        )}
                        {isSuperAdmin ? (
                            <button
                                onClick={() => handleStatusToggle(member.id, member.currentPosition.status)}
                                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                                title={`Switch to ${member.currentPosition.status === 'active' ? 'inactive' : 'active'}`}
                            >
                                {member.currentPosition.status === 'active' ?
                                    <ToggleRight className="h-4 w-4 text-green-600" /> :
                                    <ToggleLeft className="h-4 w-4 text-gray-400" />
                                }
                            </button>
                        ) : (
                            <span title="Contact Dynamix Zone for access this" className="inline-block">
                                <button
                                    className="p-1.5 rounded-lg opacity-40 cursor-not-allowed"
                                    disabled
                                >
                                    {member.currentPosition.status === 'active' ?
                                        <ToggleRight className="h-4 w-4 text-green-600" /> :
                                        <ToggleLeft className="h-4 w-4 text-gray-400" />
                                    }
                                </button>
                            </span>
                        )}
                    </div>
                </div>

                <div className="space-y-3 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Mail className="h-4 w-4" />
                        <span>{member.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Phone className="h-4 w-4" />
                        <span>{member.phone}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4" />
                        <span>{member.currentPosition.department} - {member.currentPosition.designation}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <DollarSign className="h-4 w-4" />
                        <span>${member.currentPosition.salary.toLocaleString()}</span>
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
                            {member.role.toUpperCase()}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(member.currentPosition.status)}`}>
                            {member.currentPosition.status}
                        </span>
                    </div>
                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <Shield className="h-3 w-3" />
                        <span>{getAuthorityCount(member.authorities)} permissions</span>
                    </div>
                </div>
            </div>
        </Card>
    );

    const StaffTableRow: React.FC<{ member: Staff }> = ({ member }) => (
        <tr className="hover:bg-gray-50 transition-colors duration-200">
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-white" />
                    </div>
                    <div>
                        <div className="text-sm font-medium text-gray-900">
                            {member.firstName} {member.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{member.staffId}</div>
                    </div>
                </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{member.email}</div>
                <div className="text-sm text-gray-500">{member.phone}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{member.currentPosition.department}</div>
                <div className="text-sm text-gray-500">{member.currentPosition.designation}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
                    {member.role.toUpperCase()}
                </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(member.currentPosition.status)}`}>
                    {member.currentPosition.status}
                </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                ${member.currentPosition.salary.toLocaleString()}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                    <Shield className="h-3 w-3" />
                    <span>{getAuthorityCount(member.authorities)}</span>
                </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center space-x-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setViewingStaff(member)}
                    >
                        <Eye className="h-4 w-4" />
                    </Button>
                    {isSuperAdmin ? (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onEdit(member)}
                        >
                            <Edit className="h-4 w-4" />
                        </Button>
                    ) : (
                        <span title="Contact Dynamix Zone for access this" className="inline-block">
                            <Button
                                variant="secondary"
                                size="sm"
                                className="opacity-40 cursor-not-allowed"
                                disabled
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                        </span>
                    )}
                    {isSuperAdmin ? (
                        <button
                            onClick={() => handleStatusToggle(member.id, member.currentPosition.status)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                            title={`Switch to ${member.currentPosition.status === 'active' ? 'inactive' : 'active'}`}
                        >
                            {member.currentPosition.status === 'active' ?
                                <ToggleRight className="h-4 w-4 text-green-600" /> :
                                <ToggleLeft className="h-4 w-4 text-gray-400" />
                            }
                        </button>
                    ) : (
                        <span title="Contact Dynamix Zone for access this" className="inline-block">
                            <button
                                className="p-1.5 rounded-lg opacity-40 cursor-not-allowed"
                                disabled
                            >
                                {member.currentPosition.status === 'active' ?
                                    <ToggleRight className="h-4 w-4 text-green-600" /> :
                                    <ToggleLeft className="h-4 w-4 text-gray-400" />
                                }
                            </button>
                        </span>
                    )}
                </div>
            </td>
        </tr>
    );

    return (
        <div className="space-y-5">
            {/* Page header */}
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Staff</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Manage your school staff and their permissions.</p>
                </div>
                {onAddStaff && (
                    <button onClick={onAddStaff} className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-colors">
                        <Plus className="h-4 w-4" />Create Staff
                    </button>
                )}
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-blue-50 text-blue-500 shrink-0"><Users className="h-5 w-5" /></div>
                    <div><p className="text-xs text-gray-500 font-medium">Total Staff</p><p className="text-2xl font-bold text-gray-900 mt-0.5">{summary?.total ?? staff.length}</p></div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-600 shrink-0"><UserCheck className="h-5 w-5" /></div>
                    <div><p className="text-xs text-gray-500 font-medium">Active Staff</p><p className="text-2xl font-bold text-gray-900 mt-0.5">{summary?.active ?? staff.filter(s => s.currentPosition.status === 'active').length}</p></div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-red-50 text-red-500 shrink-0"><UserX className="h-5 w-5" /></div>
                    <div><p className="text-xs text-gray-500 font-medium">Inactive Staff</p><p className="text-2xl font-bold text-gray-900 mt-0.5">{summary?.inactive ?? staff.filter(s => s.currentPosition.status === 'inactive').length}</p></div>
                </div>
            </div>

            {/* Table card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Toolbar */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h2 className="text-base font-bold text-gray-900">Staff Information</h2>
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search" className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 w-48" />
                        </div>
                        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white">
                            <option value="all">All Roles</option>
                            <option value="admin">Admin</option>
                            <option value="hr">HR</option>
                            <option value="accountant">Accountant</option>
                            <option value="principal">Principal</option>
                            <option value="clerk">Clerk</option>
                        </select>
                        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white">
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="resigned">Resigned</option>
                        </select>
                        <button className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"><RefreshCw className="h-4 w-4" /></button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-16">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 bg-gray-50/60">
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Staff Member</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Position</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Salary</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Permissions</th>
                                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {paginatedStaff.length === 0 ? (
                                    <tr><td colSpan={8} className="px-5 py-12 text-center text-gray-400 text-sm"><Users className="h-8 w-8 mx-auto mb-2 opacity-30" />No staff found</td></tr>
                                ) : paginatedStaff.map((member) => (
                                    <StaffTableRow key={member.id} member={member} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination footer */}
                <div className="flex items-center justify-between px-5 py-3.5 border-t border-gray-100 bg-gray-50/40">
                    <span className="text-xs text-gray-500">
                        {filteredStaff.length === 0 ? '0' : `${startIndex + 1}-${Math.min(startIndex + itemsPerPage, filteredStaff.length)}`} of {filteredStaff.length} items
                    </span>
                    <div className="flex items-center gap-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
                            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button className="w-7 h-7 rounded-lg bg-red-500 text-white text-xs font-bold flex items-center justify-center">{currentPage}</button>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}
                            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                            <ChevronRight className="h-4 w-4" />
                        </button>
                        <select value={itemsPerPage} onChange={e => handleItemsPerPageChange(e.target.value)}
                            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white">
                            <option value={10}>10 / page</option>
                            <option value={25}>25 / page</option>
                            <option value={50}>50 / page</option>
                        </select>
                    </div>
                </div>
            </div>



            {/* Read-only View Modal */}
            {viewingStaff && (
                <Modal
                    isOpen={true}
                    onClose={() => setViewingStaff(null)}
                    title={`View Staff - ${viewingStaff.firstName} ${viewingStaff.lastName}`}
                    size="full"
                >
                    <div className="space-y-8">
                        {/* Basic Information */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                <User className="h-6 w-6 mr-3 text-emerald-600" />
                                Basic Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Staff ID</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.staffId}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.firstName}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.lastName}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.email}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.phone}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.dateOfBirth.toLocaleDateString()}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900 capitalize">
                                        {viewingStaff.gender}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900 capitalize">
                                        {viewingStaff.role}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Professional Information */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6">Professional Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Education</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.qualification.education}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Experience (Years)</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.experience.totalYears}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.currentPosition.department}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.currentPosition.designation}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Joining Date</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.currentPosition.joiningDate.toLocaleDateString()}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Salary</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        ${viewingStaff.currentPosition.salary.toLocaleString()}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900 capitalize">
                                        {viewingStaff.currentPosition.status}
                                    </div>
                                </div>
                            </div>

                            {/* Certifications */}
                            {viewingStaff.qualification.certifications.length > 0 && (
                                <div className="mt-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-3">Certifications</label>
                                    <div className="flex flex-wrap gap-2">
                                        {viewingStaff.qualification.certifications.map((cert, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-emerald-100 text-emerald-800"
                                            >
                                                {cert}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>

                        {/* Address Information */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6">Address Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.address.street}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.address.city}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.address.state}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.address.zipCode}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.address.country}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Emergency Contact */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6">Emergency Contact</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Name</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.emergencyContact.name}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.emergencyContact.relationship}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.emergencyContact.phone}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.emergencyContact.email || 'Not provided'}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Bank Details */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6">Bank Details</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.bankDetails.accountNumber}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.bankDetails.bankName}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.bankDetails.ifscCode}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Account Holder Name</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingStaff.bankDetails.accountHolderName}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Authorities */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                <Shield className="h-6 w-6 mr-3 text-emerald-600" />
                                Permissions ({getAuthorityCount(viewingStaff.authorities)})
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {Object.entries(viewingStaff.authorities).map(([authority, hasPermission]) => (
                                    <div key={authority} className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200">
                                        <div className={`w-3 h-3 rounded-full ${hasPermission ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900 capitalize">
                                                {authority.replace(/([A-Z])/g, ' $1').trim()}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {hasPermission ? 'Granted' : 'Not granted'}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>

                        {/* Modal Actions */}
                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                            <Button
                                variant="secondary"
                                onClick={() => setViewingStaff(null)}
                            >
                                Close
                            </Button>
                            {isSuperAdmin ? (
                                <Button
                                    onClick={() => {
                                        setViewingStaff(null);
                                        onEdit(viewingStaff);
                                    }}
                                >
                                    Edit Staff
                                </Button>
                            ) : (
                                <span title="Contact Dynamix Zone for access this" className="inline-block">
                                    <Button
                                        className="opacity-40 cursor-not-allowed"
                                        disabled
                                    >
                                        Edit Staff
                                    </Button>
                                </span>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default StaffView;
